import {Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, ValidationErrors, AbstractControl, Validators, FormGroup, FormBuilder, FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {delay, distinctUntilChanged, tap} from 'rxjs/operators';
import {
  compareMatch,
  Address2ComponentValue,
  UploadedDocumentRef,
  CountryOfCitizenshipOptions,
  CountryOfCitizenshipValue,
  IdentificationComponentValue,
  IndividualFormValue,
  PlacesDetailsFn,
  PlacesSearchResultFn,
  PropertyOwnerWithAddressValue,
  TitleSelectionValue,
  UploadFileValue,
  YesNoValue,
} from '@portal-workspace/grow-shared-library';
import {Moment} from 'moment';
import {UntilDestroy} from '@ngneat/until-destroy';
import {
  setupUntilDestroy,
  getAddress2ComponentValueFormControlValueFn,
  formControlErrorKeys,
  formControlErrorMessage,
} from '@portal-workspace/grow-ui-library';
import {Subject, Subscription} from 'rxjs';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatOptionModule} from '@angular/material/core';
import {MatDividerModule} from '@angular/material/divider';
import {MobileComponent, MobileComponentEvent} from '../mobile-component/mobile.component';
import {EmailComponent, EmailComponentEvent} from '../common fields/email.component';
import {PropertyOwnerWithAddressComponent} from '../property-owner-with-address-component/property-owner-with-address.component';
import {YesNoComponent} from '../yes-no-component/yes-no.component';
import {CustomAddressComponent} from '../address-component/custom-address.component';
import {DatepickerComponent} from '../datepicker-component/datepicker.component';
import {NameComponent} from '../name-component/name.component';
import {MarkDirective} from '../../directives/mark-as-dirty.directive';
import {TitleSelectionComponent} from '../title-selection-component/title-selection.component';
import {IdentificationComponent} from '../identification-component/identification.component';
import {UploadFileComponent} from '../upload-file-component/upload-file.component';
import {DisableControlDirective} from '../../directives/disable-control.directive';
import {AuthService} from 'apps/portal2/src/app/service/auth.service';
import {
  UploadedFilesListComponent,
  UploadedFilesListComponentEvent,
} from '../upload-file-component/uploaded-files-list.component';

export interface IndividualFormComponentEvent {
  valid: boolean;
  value: IndividualFormValue;
}

// Unified editor for a single individual (director / trustee / partner / member / sole trader /
// guarantor / KYC contact). Gender and per-person guarantor selection are deliberately not part
// of this form: guarantor is selected on the KYB Key People screen.
@UntilDestroy({arrayName: 'subscriptions'})
@Component({
  selector: 'individual-form',
  templateUrl: './individual-form.component.html',
  styleUrls: ['./individual-form.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => IndividualFormComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => IndividualFormComponent), multi: true},
    {provide: MARK, useExisting: forwardRef(() => IndividualFormComponent)},
  ],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatDividerModule,
    TitleSelectionComponent,
    MarkDirective,
    NameComponent,
    DatepickerComponent,
    CustomAddressComponent,
    YesNoComponent,
    PropertyOwnerWithAddressComponent,
    EmailComponent,
    MobileComponent,
    IdentificationComponent,
    UploadFileComponent,
    UploadedFilesListComponent,
    DisableControlDirective,
  ],
})
export class IndividualFormComponent extends AbstractControlValueAccessor<IndividualFormValue> implements OnInit, OnChanges, Mark, Validator {

  errorKeys = formControlErrorKeys;
  errorMessage = formControlErrorMessage;
  subscriptions: Subscription[] = [];
  markObservable: Subject<boolean> = new Subject<boolean>();
  placesSearchResultFn: PlacesSearchResultFn | null = null;
  placesDetailsFn: PlacesDetailsFn | null = null;
  getAddress2ComponentValueFormControlValueFn = getAddress2ComponentValueFormControlValueFn;

  countryOptions = CountryOfCitizenshipOptions;

  // header eg. "John Citizen - Director"; hidden when null
  @Input({required: false}) roleLabel: string | null = null;
  @Input({required: false}) withPropertyOwnerAddress = true;
  @Input({required: false}) showPropertyOwner = true;
  @Input({required: false}) showAddressForm = true;
  // shows the ID type + number fields (licence or passport), both required while shown
  @Input({required: false}) showIdentification = true;
  // shows the licence / ID document upload; parents without an upload flow (eg. KYC tab) hide it
  @Input({required: false}) showIdDocumentUpload = true;
  // KYC tab passes false: legacy records have no citizenship, requiring it would invalidate them on load
  @Input({required: false}) requireCitizenship = true;
  // Parents set this when the person's role is guarantor or guarantor/signer. The alert itself
  // stays off until a country other than Australia is selected, and it never blocks save.
  @Input({required: false}) showResidencyProofAlert = false;
  // an ID document is already held against this individual: the file itself is never read back
  // into the form, so without this the requirement below could never be satisfied on an edit
  @Input({required: false}) idDocumentProvided = false;
  @Input({required: false}) existingIdDocuments: UploadedDocumentRef[] = [];
  @Output() events: EventEmitter<IndividualFormComponentEvent> = new EventEmitter<IndividualFormComponentEvent>();
  @Output() idDocumentDelete = new EventEmitter<UploadedDocumentRef>();

  idDocumentRequired = true;

  // A licence is commonly photographed front and back, and a passport can run to more than one
  // page, so the upload takes several files rather than one.
  readonly maxIdDocuments = 6;

  onDeleteIdDocument(event: UploadedFilesListComponentEvent): void {
    this.idDocumentDelete.emit(event.deletedFile);
  }

  formControlTitle: FormControl<TitleSelectionValue>;
  formControlFirstName: FormControl<string | null>;
  formControlMiddleName: FormControl<string | null>;
  formControlLastName: FormControl<string | null>;
  formControlDob: FormControl<Moment | null>;
  formControlHasAlias: FormControl<YesNoValue>;
  formControlAliasNames: FormControl<string | null>;
  formControlCountryOfCitizenship: FormControl<CountryOfCitizenshipValue>;
  formControlEmail: FormControl<string | null>;
  formControlMobileNumber: FormControl<string | null>;
  formControlResidentialAddress: FormControl<Address2ComponentValue>;
  formControlPropertyOwner: FormControl<PropertyOwnerWithAddressValue | YesNoValue>;
  formControlIdentification: FormControl<IdentificationComponentValue>;
  formControlIdDocument: FormControl<UploadFileValue>;

  formGroup: FormGroup<{
    title: FormControl<TitleSelectionValue>,
    firstName: FormControl<string | null>,
    middleName: FormControl<string | null>,
    lastName: FormControl<string | null>,
    dob: FormControl<Moment | null>,
    hasAlias: FormControl<YesNoValue>,
    aliasNames: FormControl<string | null>,
    countryOfCitizenship: FormControl<CountryOfCitizenshipValue>,
    email: FormControl<string | null>,
    mobileNumber: FormControl<string | null>,
    residentialAddress: FormControl<Address2ComponentValue>,
    propertyOwner: FormControl<PropertyOwnerWithAddressValue | YesNoValue>,
    identification: FormControl<IdentificationComponentValue>,
    idDocument: FormControl<UploadFileValue>,
  }>;

  constructor(private formBuilder: FormBuilder, private authService: AuthService) {
    super();
    this.formControlTitle = this.formBuilder.control(null, [Validators.required]);
    this.formControlFirstName = this.formBuilder.control(null, [Validators.required]);
    this.formControlMiddleName = this.formBuilder.control(null);
    this.formControlLastName = this.formBuilder.control(null, [Validators.required]);
    this.formControlDob = this.formBuilder.control(null, [Validators.required]);
    this.formControlHasAlias = this.formBuilder.control(false, [Validators.required]);
    this.formControlAliasNames = this.formBuilder.control(null);
    this.formControlCountryOfCitizenship = this.formBuilder.control(null, [Validators.required]);
    this.formControlEmail = this.formBuilder.control(null, [Validators.required]);
    this.formControlMobileNumber = this.formBuilder.control(null, [Validators.required]);
    this.formControlResidentialAddress = this.formBuilder.control(null, [Validators.required]);
    this.formControlPropertyOwner = this.formBuilder.control(null, [Validators.required]);
    this.formControlIdentification = this.formBuilder.control(null, [Validators.required]);
    this.formControlIdDocument = this.formBuilder.control(null, [Validators.required]);
    this.formGroup = this.formBuilder.group({
      title: this.formControlTitle,
      firstName: this.formControlFirstName,
      middleName: this.formControlMiddleName,
      lastName: this.formControlLastName,
      dob: this.formControlDob,
      hasAlias: this.formControlHasAlias,
      aliasNames: this.formControlAliasNames,
      countryOfCitizenship: this.formControlCountryOfCitizenship,
      email: this.formControlEmail,
      mobileNumber: this.formControlMobileNumber,
      residentialAddress: this.formControlResidentialAddress,
      propertyOwner: this.formControlPropertyOwner,
      identification: this.formControlIdentification,
      idDocument: this.formControlIdDocument,
    });
    this.placesSearchResultFn = this.authService.getGooglePlacesSearchResultFn;
    this.placesDetailsFn = this.authService.getGooglePlacesDetailsFn;
  }

  ngOnInit(): void {
    setupUntilDestroy(this);

    if (!this.withPropertyOwnerAddress) {
      this.formControlPropertyOwner.setValue(false);
    }
    if (!this.showPropertyOwner) {
      this.formControlPropertyOwner.clearValidators();
      this.formControlPropertyOwner.updateValueAndValidity();
    }
    if (!this.requireCitizenship) {
      this.formControlCountryOfCitizenship.clearValidators();
      this.formControlCountryOfCitizenship.updateValueAndValidity();
    }
    this.syncIdentificationValidators();

    this.subscriptions.push(this.formControlHasAlias.valueChanges.pipe(
      distinctUntilChanged(compareMatch),
      tap(hasAlias => {
        if (hasAlias) {
          this.formControlAliasNames.setValidators([Validators.required]);
        } else {
          this.formControlAliasNames.clearValidators();
          this.formControlAliasNames.setValue(null);
        }
        this.formControlAliasNames.updateValueAndValidity();
      })
    ).subscribe());

    this.subscriptions.push(this.formGroup.valueChanges.pipe(
      delay(0),
      distinctUntilChanged(compareMatch),
      tap(() => {
        // always propagate the (possibly partial) value; validity is surfaced separately through
        // validate() so embedding editors (eg. KYC tab) can diff values of pre-populated records
        const value = this.formGroupToValue();
        this.propagateChange(value);
        this.events.emit({valid: this.formGroup.valid, value});
      })
    ).subscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showIdentification'] || changes['showIdDocumentUpload'] || changes['idDocumentProvided']) {
      this.syncIdentificationValidators();
    }
  }

  // ID type, number and document are required wherever they are shown. Re-run on input changes
  // because embedders resolve idDocumentProvided asynchronously (eg. the individual details list
  // reads it off the application's documents).
  private syncIdentificationValidators(): void {
    if (this.showIdentification) {
      this.formControlIdentification.setValidators([Validators.required]);
    } else {
      this.formControlIdentification.clearValidators();
    }
    this.formControlIdentification.updateValueAndValidity();

    this.idDocumentRequired = this.showIdentification && this.showIdDocumentUpload && !this.idDocumentProvided;
    if (this.idDocumentRequired) {
      this.formControlIdDocument.setValidators([Validators.required]);
    } else {
      this.formControlIdDocument.clearValidators();
    }
    this.formControlIdDocument.updateValueAndValidity();
  }

  // email/mobile child components keep format/duplicate errors internal and propagate null instead;
  // track their error events so embedders see an invalid outer control rather than a silent null
  emailComponentErrors: ValidationErrors | null = null;
  mobileComponentErrors: ValidationErrors | null = null;
  private onValidatorChangeFn?: () => void;

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChangeFn = fn;
  }

  onEmailEvent(event: EmailComponentEvent) {
    this.emailComponentErrors = event.errors;
    this.onValidatorChangeFn?.();
  }

  onMobileEvent(event: MobileComponentEvent) {
    this.mobileComponentErrors = event.errors;
    this.onValidatorChangeFn?.();
  }

  validate(_: AbstractControl): ValidationErrors | null {
    if (this.formGroup.valid && !this.emailComponentErrors && !this.mobileComponentErrors) {
      return null;
    }
    return {individualForm: true};
  }

  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
    if (isDisabled) {
      this.formGroup.disable({emitEvent: false});
    } else {
      this.formGroup.enable({emitEvent: false});
    }
  }

  showCitizenshipResidencyAlert(): boolean {
    if (!this.showResidencyProofAlert) {
      return false;
    }
    const country = this.formControlCountryOfCitizenship.value;
    return !!country && country.code !== 'AU';
  }

  headerName(): string {
    const firstName = this.formControlFirstName.value ?? '';
    const lastName = this.formControlLastName.value ?? '';
    const name = `${firstName} ${lastName}`.trim();
    return name.length ? name : 'Individual';
  }

  doWriteValue(v: IndividualFormValue | null | undefined): void {
    if (v) {
      this.formControlTitle.setValue(v.title);
      this.formControlFirstName.setValue(v.firstName);
      this.formControlMiddleName.setValue(v.middleName ?? null);
      this.formControlLastName.setValue(v.lastName);
      this.formControlDob.setValue(v.dob);
      this.formControlHasAlias.setValue(v.hasAlias ?? false);
      this.formControlAliasNames.setValue(v.aliasNames ?? null);
      this.formControlCountryOfCitizenship.setValue(this.matchCountryOption(v.countryOfCitizenship));
      this.formControlEmail.setValue(v.email);
      this.formControlMobileNumber.setValue(v.mobileNumber);
      this.formControlResidentialAddress.setValue(v.residentialAddress);
      this.formControlPropertyOwner.setValue(v.propertyOwner ?? (this.withPropertyOwnerAddress ? null : false));
      this.formControlIdentification.setValue(v.identification ?? null);
      this.formControlIdDocument.setValue(v.idDocument ?? null);
    }
  }

  // preserve null (legacy records without citizenship, or unrecognised codes) so nothing is
  // silently defaulted and value diffing in editors stays accurate
  private matchCountryOption(v: CountryOfCitizenshipValue): CountryOfCitizenshipValue {
    if (!v) {
      return null;
    }
    return this.countryOptions.find(c => c.code === v.code) ?? null;
  }

  private formGroupToValue(): IndividualFormValue {
    return {
      title: this.formControlTitle.value,
      firstName: this.formControlFirstName.value!,
      middleName: this.formControlMiddleName.value ?? '',
      lastName: this.formControlLastName.value!,
      dob: this.formControlDob.value!,
      hasAlias: !!this.formControlHasAlias.value,
      aliasNames: this.formControlHasAlias.value ? (this.formControlAliasNames.value ?? null) : null,
      countryOfCitizenship: this.formControlCountryOfCitizenship.value,
      email: this.formControlEmail.value!,
      mobileNumber: this.formControlMobileNumber.value!,
      residentialAddress: this.formControlResidentialAddress.value,
      propertyOwner: this.formControlPropertyOwner.value,
      identification: this.formControlIdentification.value,
      idDocument: this.formControlIdDocument.value,
    };
  }

  mark() {
    this.formGroup.markAllAsTouched();
    this.markObservable.next(true);
  }
}
